import type { PlannerTransferAssistance } from '@travel-planner/shared/utils/planner-transfers';
import type { PlannerProductsInput } from './host';
import type { AdvisorPoint, AdvisorProduct } from './types';
import { normalizePlaceKey } from '@travel-planner/shared/pricing';
import { haversineKm } from '@travel-planner/shared/utils/distance';

// T-423 — the VIP airport assistance an arrival or departure can add: the
// DMC's own product at the airport the trip lands at or leaves from.

/** What the catalogue is searched for (title, any language). */
export const ASSISTANCE_SEARCH = 'vip airport assistance';
/** Enough to hold every airport a country's DMC covers. */
export const ASSISTANCE_LIMIT = 40;
/** An airport further than this from the hotel is not the trip's. */
export const ASSISTANCE_MAX_KM = 250;
/** What an assistance with no product reads; the DMC books it. */
export const ASSISTANCE_ON_REQUEST_TITLE = 'VIP airport assistance';

const ASSISTANCE_WORDS = new Set(['assistance', 'assistencia', 'asistencia']);
// A connecting flight's assistance never meets an arrival or a departure.
const CONNECTION_WORDS = new Set(['connection', 'conexao', 'conexion']);
const GENERIC_WORDS = new Set([
  'vip',
  'airport',
  'aeroporto',
  'aeropuerto',
  ...ASSISTANCE_WORDS,
  'transfer',
]);

/** The consultant catalogue's assistance search in a country. */
export const assistanceProductsInput = (
  country: string
): PlannerProductsInput => ({
  type: 'experiences',
  country,
  selectedRegionIds: [],
  selectedTags: [],
  tags: [],
  regions: [],
  page: 1,
  search: ASSISTANCE_SEARCH,
  limit: ASSISTANCE_LIMIT,
});

const words = (value: string | null | undefined) =>
  normalizePlaceKey(value ?? '').split(' ');

/** A title naming a VIP airport assistance for an arrival or departure. */
export const isAssistanceTitle = (value: string): boolean => {
  const title = words(value);
  return (
    title.includes('vip') &&
    title.some((word) => ASSISTANCE_WORDS.has(word)) &&
    !title.some((word) => CONNECTION_WORDS.has(word))
  );
};

/**
 * The assistance at the trip's airport: the one nearest the hotel (or the
 * place the agent picked) within `ASSISTANCE_MAX_KM`; without a point, the
 * one naming the chapter's town. None fits: null.
 */
export const pickAssistanceProduct = (
  products: readonly AdvisorProduct[],
  where: { near: AdvisorPoint | null; town: string }
): AdvisorProduct | null => {
  const pool = products.filter((product) => isAssistanceTitle(product.title));
  const { near } = where;
  if (near) {
    const nearest = pool
      .flatMap((product) =>
        product.location
          ? [
              {
                product,
                km: haversineKm(
                  [near.lat, near.lng],
                  [product.location.lat, product.location.lng]
                ),
              },
            ]
          : []
      )
      .sort((a, b) => a.km - b.km)[0];
    return nearest && nearest.km <= ASSISTANCE_MAX_KM ? nearest.product : null;
  }
  const town = words(where.town).filter(
    (word) => word.length > 2 && !GENERIC_WORDS.has(word)
  );
  if (town.length === 0) return null;
  return (
    pool.find((product) => {
      const own = new Set(words(`${product.title} ${product.subtitle ?? ''}`));
      return town.some((word) => own.has(word));
    }) ?? null
  );
};

/** What the draft keeps: the product's name and prices, else on request. */
export const toTransferAssistance = (
  product: AdvisorProduct | null,
  route: string
): PlannerTransferAssistance =>
  product
    ? {
        productId: product.id,
        route,
        title: product.title,
        clientPrice: product.clientPrice,
        childPrice: product.childPrice ?? null,
        currency: product.currency,
        pricingType: product.pricingType ?? null,
      }
    : {
        productId: null,
        route,
        title: ASSISTANCE_ON_REQUEST_TITLE,
        clientPrice: null,
        childPrice: null,
        currency: null,
        pricingType: null,
      };
